import apiGraph, { graphGet } from "@/helpers/api/apiGraph";
import DriveItemHelper from "@/models/helpers/DriveItemHelper";
import Permission from "@/models/Documents/Permission";

import { useDocumentStore } from "@/stores/documents";
import { useFeaturesStore } from "@/stores/features";
import { useSectionsStore } from "@/stores/sections";

/**
 * @category Store
 * @subcategory Documents
 * @module Documents
 * @name DocumentsActions
 */

export default {
  //////////////////////// Higher Abstraction ////////////////////////
  /**
   * Higher abstraction for loading documents
   * @param { object } vuex Vuex
   * @param { vuex } vuex.dispatch Dispatch
   * @param { object } payload Payload
   * @param { string } payload.driveId id of drive to load
   * @param { string } payload.itemId id of driveItem to load
   * @returns { Promise<void> } Promise
   */
  async loadDocuments({ dispatch }, { driveId, itemId }) {
    if (itemId) {
      const documentStore = useDocumentStore();
      return documentStore.loadDriveItem(driveId, itemId);
    } else {
      return await dispatch("loadDrive", driveId);
    }
  },

  //////////////////////// Drive ////////////////////////

  /**
   * Load a drive root and children
   * @param { object } vuex Vuex
   * @param { vuex } vuex.commit Commit
   * @param { string } driveId DriveId identifying drive
   * @returns { object[] } Array with drive items
   */
  async loadDrive({ commit }, driveId) {
    try {
      const result = await graphGet(`${DriveItemHelper.getDriveUri(driveId)}/root/?$expand=children`);
      //dispatch("loadPermissionsForMultipleDriveItems", { driveItems: result.children }); // This wears out MS Graph causing throtteling 429

      const featureStore = useFeaturesStore();

      if (featureStore.isFeatureEnabled("DocumentSensitivityLabels")) {
        const responseChildren = await apiGraph.get(
          `${DriveItemHelper.getDriveUri(driveId)}/root/children?$expand=listItem($expand=fields($select=_DisplayName))`
        );

        result.children = responseChildren.data.value;
      }

      commit("loadDrive", { data: result });
      return result;
    } catch {
      return null;
    }
  },

  setDriveRoot({ commit }, { driveId, itemId, displayName }) {
    commit("setDriveRoot", { driveId, itemId });
    commit("setDriveRootDisplayName", { driveId, displayName });
  },

  //////////////////////// DriveItems ////////////////////////

  //
  //
  //

  // eslint-disable-next-line no-empty-pattern
  async fetchWebUrl({}, { driveId, itemId }) {
    const response = await apiGraph.get(`/drives/${driveId}/items/${itemId}`);
    return response.data.webUrl;
  },

  //////////////////////// Sharing ////////////////////////

  async shareToPeople({ dispatch, getters }, { driveId, itemId, people, write }) {
    const peoplePermissions = getters.PERMISSION_OBJECTS_FOR_PERSONLIST_ON_DRIVEITEM({
      persons: people,
      driveId,
      itemId
    });

    const permissionIdsToBeRemoved = peoplePermissions
      .filter(pp => pp.permissionOnItem.myPermission !== Permission.nctPermission.NONE)
      .map(pp => pp.permissionOnItem.id);

    await dispatch("removeSharing", {
      driveId,
      itemId,
      sharingPermissionIds: [...permissionIdsToBeRemoved],
      doNotReload: true
    });

    const recipients = people.map(p => {
      return {
        email: p.emailSchoolAddress
      };
    });

    let requestDriveId = null;

    if (driveId !== getters["MY_DRIVE_ROOT"]) {
      requestDriveId = driveId;
    }

    await apiGraph.post(
      `${DriveItemHelper.getDriveUri(requestDriveId)}/items/${DriveItemHelper.getItemId(itemId)}/invite`,
      {
        requireSignIn: true,
        sendInvitation: false,
        roles: write ? ["write"] : ["read"],
        recipients: recipients
      }
    );

    const documentStore = useDocumentStore();

    await documentStore.reloadDocumentPermissions(driveId, itemId);
  },

  // eslint-disable-next-line no-empty-pattern
  async removeSharing({}, { driveId, itemId, sharingPermissionIds, doNotReload }) {
    try {
      const promises = sharingPermissionIds.map(async id => {
        if (id) {
          return apiGraph
            .delete(
              `${DriveItemHelper.getDriveUri(driveId)}/items/${DriveItemHelper.getItemId(itemId)}/permissions/${id}`
            )
            .finally(async () => {
              if (!doNotReload) {
                const documentStore = useDocumentStore();

                await documentStore.reloadDocumentPermissions(driveId, itemId);
              }
            });
        }
      });

      await Promise.all(promises);
      // eslint-disable-next-line no-empty
    } catch {}
  },

  //////////////////////// Sections ////////////////////////

  async loadSectionDrivesRoot({ commit }, { sectionId }) {
    const sectionsStore = useSectionsStore();

    const response = await apiGraph.get(
      `/groups/${sectionsStore(sectionId)?.extSysApiSectionId}/drive/items/root:/General/?$expand=children`
    );

    const sectionDriveItem = response.data;
    sectionDriveItem.name = "root";
    delete sectionDriveItem.parentReference.id;

    commit("loadDrive", {
      data: sectionDriveItem
    });
    commit("setSectionDriveRoot", {
      sectionId: sectionId,
      driveId: sectionDriveItem.parentReference.driveId,
      itemId: sectionDriveItem.id
    });
  }
};
